/**
 * unitsCatalog
 * ---------------------------------------------------------------------------
 * Single access point for the bundled unit snapshot (src/unitsData.json,
 * regenerated by scripts/refresh-units.ps1). The snapshot now carries, per
 * unit, the Salesforce record `id`, the EN/FR descriptions, AND the public
 * photo URLs (compressed as suffixes of a shared `imgBase` prefix).
 *
 * WHY: the AWAC UnitCarousel envelope is deliberately FACT-ONLY — every
 * envelope character is an output token the agent must echo verbatim, and
 * embedding photo URLs made unit answers take 15-20s. The client instead
 * joins each envelope unit to this snapshot (by `id`, falling back to unit
 * number / name) to get photos and rich descriptions locally, instantly.
 *
 * Tolerates BOTH snapshot shapes:
 *   - new: { imgBase, units: [ { id, name, unit, ..., images: [suffix|url] } ] }
 *   - old: [ { unit, ... } ]  (no ids/photos — lookups just return less)
 */
import unitsData from "./unitsData.json";

const IS_NEW_SHAPE = unitsData && !Array.isArray(unitsData) && Array.isArray(unitsData.units);

export const UNITS = IS_NEW_SHAPE ? unitsData.units : (Array.isArray(unitsData) ? unitsData : []);
export const IMG_BASE = (IS_NEW_SHAPE && typeof unitsData.imgBase === "string") ? unitsData.imgBase : "";
// versionId(15) -> { t: Title, d: Description, df: Description_FR__c }
export const IMG_META = (IS_NEW_SHAPE && unitsData.imgMeta && typeof unitsData.imgMeta === "object")
    ? unitsData.imgMeta
    : {};

const byId = new Map();
const byUnitNumber = new Map();
for (const u of UNITS) {
    if (u.id) {
        byId.set(String(u.id), u);
        // 15-char <-> 18-char Salesforce id tolerance.
        byId.set(String(u.id).slice(0, 15), u);
    }
    if (u.unit != null) {
        byUnitNumber.set(String(u.unit).trim(), u);
    }
}

/**
 * Finds a snapshot unit for an envelope unit record (or a plain identifier).
 * Join order: Salesforce id -> unitNumber -> digits inside the name
 * ("Unit 204" -> "204").
 * @param {object|string} ref - envelope unit ({id, unitNumber, name}) or an id/number string
 * @returns {object|null}
 */
export function findUnit(ref) {
    if (ref == null) {
        return null;
    }
    if (typeof ref === "string" || typeof ref === "number") {
        const s = String(ref).trim();
        return byId.get(s) || byId.get(s.slice(0, 15)) || byUnitNumber.get(s) || null;
    }
    const id = ref.id || ref.Id;
    if (id) {
        const hit = byId.get(String(id)) || byId.get(String(id).slice(0, 15));
        if (hit) {
            return hit;
        }
    }
    const unitNo = ref.unitNumber || ref.Unit_Number__c || ref.unit;
    if (unitNo != null && byUnitNumber.has(String(unitNo).trim())) {
        return byUnitNumber.get(String(unitNo).trim());
    }
    const name = ref.name || ref.Name || "";
    const m = /(\d{2,5})/.exec(String(name));
    if (m && byUnitNumber.has(m[1])) {
        return byUnitNumber.get(m[1]);
    }
    return null;
}

/**
 * Full public photo URLs for a unit (snapshot suffixes re-expanded with the
 * shared imgBase prefix; absolute URLs pass through).
 * @param {object|string} ref - same as findUnit
 * @returns {Array<string>}
 */
export function unitImages(ref) {
    const u = findUnit(ref);
    if (!u || !Array.isArray(u.images)) {
        return [];
    }
    return u.images
        .filter((s) => typeof s === "string" && s)
        .map((s) => (s.startsWith("http") ? s : IMG_BASE + s));
}

/**
 * Language-aware caption for a single photo, from the ContentVersion the URL
 * points at (Description / Description_FR__c, falling back to Title).
 *
 * Photos are shared across units, so the snapshot stores captions ONCE in
 * `imgMeta`, keyed by the 15-char ContentVersion id that every stored image
 * suffix begins with — we recover that key from the URL rather than carrying a
 * caption on all ~1,000 unit-image pairs.
 *
 * @param {string} url - full or suffix image URL
 * @param {string} lang - "fr" | "en"
 * @returns {string} caption, or "" when the photo has none
 */
export function imageCaption(url, lang) {
    if (typeof url !== "string" || !url) {
        return "";
    }
    // Works for a bare suffix ("068...&d=...") and a full ContentDownloadUrl.
    const m = /(?:^|[?&]ids=)(068[A-Za-z0-9]{12})/.exec(url);
    const meta = m && IMG_META[m[1]];
    if (!meta) {
        return "";
    }
    const fr = String(lang || "").toLowerCase().startsWith("fr");
    return (fr ? meta.df || meta.d : meta.d || meta.df) || meta.t || "";
}

/**
 * Language-aware one-line description for a unit (marketing copy from the
 * org snapshot; FR falls back to EN and vice versa).
 * @param {object|string} ref - same as findUnit
 * @param {string} lang - "fr" | "en"
 * @returns {string}
 */
export function unitDescription(ref, lang) {
    const u = findUnit(ref);
    if (!u) {
        return "";
    }
    return (lang === "fr" ? (u.descFr || u.desc) : (u.desc || u.descFr)) || "";
}

// Premium feature flags in display order: snapshot field -> i18n label key
// (feat.* in src/awac/i18n.js). Sun exposure is a string field; "high"
// qualifies as the bright/large-windows highlight. The two kitchen flags
// (bigger countertops / bigger island) are unified into ONE feature below —
// either one reads as "more prep space" to a prospect.
const PREMIUM_FEATURES = [
    ["corner", "feat.corner"],
    ["den", "feat.den"],
    ["ensuite", "feat.ensuite"],
    ["walkIn", "feat.walkIn"],
    ["washerDryer", "feat.washerDryer"],
    ["backyard", "feat.backyard"],
    ["sideYard", "feat.sideYard"],
];

/**
 * Ordered i18n label KEYS of the premium features a unit has (counter space,
 * corner unit, den, high sun exposure, ...). Callers render t(key).
 * @param {object|string} ref - same as findUnit (envelope units work: hasDen
 *        counts even when the snapshot row is missing)
 * @returns {Array<string>}
 */
export function unitFeatureKeys(ref) {
    const u = findUnit(ref);
    const keys = [];
    // Unified kitchen highlight: bigger countertops OR a bigger island.
    if (u && (u.counters === true || u.island === true)) {
        keys.push("feat.counterSpace");
    }
    for (const [field, key] of PREMIUM_FEATURES) {
        if (u && u[field] === true) {
            keys.push(key);
        } else if (field === "den" && !u && ref && typeof ref === "object" && ref.hasDen === true) {
            keys.push(key);
        }
    }
    if (u && typeof u.sun === "string" && u.sun.toLowerCase().includes("high")) {
        keys.push("feat.sun");
    }
    return keys;
}
